import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { AudioPlayer } from "../components/AudioPlayer";
import { WaitlistForm } from "../components/WaitlistForm";
import { ProductGallery, ProductPanel } from "../sections/Product";
import { WorldCard } from "../sections/Universe";
import { FAQ } from "../sections/FAQ";
import { WORLDS } from "../data/worlds";

const FarbenDetail = ({ world }) => (
  <>
    <section className="py-12 sm:py-16">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <Reveal className="max-w-2xl mb-12">
          <p className="eyebrow-label mb-5">Unsere erste Lernwelt · {world.age}</p>
          <h1 className="font-display font-medium tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.06] mb-3">
            {world.title}
          </h1>
          <p className="font-display italic text-lg sm:text-xl" style={{ color: "var(--terra)" }}>
            {world.subtitle}
          </p>
        </Reveal>
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <Reveal className="max-w-[560px] mx-auto w-full lg:sticky lg:top-28">
            <ProductGallery testPrefix="detail" />
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-[15px] sm:text-base text-[color:var(--ink-muted)] leading-relaxed mb-8">
              {world.description}
            </p>
            <ProductPanel testPrefix="detail" />
            <div className="mt-10">
              <p className="eyebrow-label mb-4">Der passende Lernsong</p>
              <AudioPlayer title={world.song.title} src={world.song.url} />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
    <FAQ compact />
  </>
);

const FutureDetail = ({ world }) => (
  <section className="py-12 sm:py-16">
    <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
      <Reveal>
        <div
          className="relative mx-auto w-[220px] aspect-[3/4] rounded-[22px] mb-10 flex flex-col items-center justify-center gap-4 overflow-hidden"
          style={{ background: world.soft, boxShadow: "0 20px 48px rgba(44,43,40,0.12)" }}
        >
          <div className="blob floaty-slow" style={{ background: `${world.accent}22`, width: "72%", height: "56%", top: "22%", left: "14%" }} />
          <span className="relative font-display italic font-semibold text-5xl" style={{ color: world.accent }}>
            {world.glyphs.join(" ")}
          </span>
          <span className="relative text-[10px] uppercase tracking-[0.24em] font-semibold" style={{ color: world.accent }}>
            Lernwelt {world.short}
          </span>
        </div>
        <span
          className="inline-block text-[11px] uppercase tracking-[0.18em] font-semibold px-4 py-2 rounded-full mb-6"
          style={{ background: world.soft, color: world.accent, border: `1px solid ${world.accent}44` }}
          data-testid="future-world-status"
        >
          {world.statusLabel}
        </span>
        <h1 className="font-display font-medium tracking-tight text-4xl sm:text-5xl leading-[1.08] mb-4">
          {world.title}
        </h1>
        <p className="text-[15px] sm:text-base text-[color:var(--ink-muted)] leading-relaxed max-w-lg mx-auto mb-10">
          {world.description}
        </p>
        <div className="flex justify-center mb-4">
          <WaitlistForm listType="community" cta="Benachrichtige mich" testPrefix="future-world" />
        </div>
        <p className="text-xs text-[color:var(--ink-muted)]">
          Wir informieren dich, sobald diese Lernwelt erscheint (Double-Opt-in).
        </p>
      </Reveal>
    </div>
  </section>
);

export default function WorldDetail() {
  const { slug } = useParams();
  const world = WORLDS.find((w) => w.slug === slug);
  if (!world) return <Navigate to="/lernwelten" replace />;
  const related = WORLDS.filter((w) => w.slug !== slug);

  return (
    <main className="pt-28" data-testid={`world-detail-${slug}`}>
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <Link to="/lernwelten" className="inline-flex items-center gap-2 text-sm font-medium text-[color:var(--ink-muted)] hover:text-[color:var(--ink)] transition-colors" data-testid="back-to-worlds">
          <ArrowLeft size={16} /> Alle Lernwelten
        </Link>
      </div>
      {world.status === "available" ? <FarbenDetail world={world} /> : <FutureDetail world={world} />}
      <section className="py-20" style={{ background: "var(--paper-deep)" }}>
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <Reveal className="mb-10">
            <h2 className="font-display font-medium tracking-tight text-3xl sm:text-4xl">
              Weitere <em style={{ color: "var(--terra)" }}>Lernwelten.</em>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {related.map((w, i) => (
              <WorldCard key={w.slug} w={w} i={i} />
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
